/* Search-engine and AI-crawler metadata for the public marketing pages: titles, descriptions, canonical and
 * hreflang links, Open Graph, JSON-LD structured data, sitemap.xml, robots.txt and llms.txt.
 * One source for both the build-time prerender (scripts/prerender.mjs) and the live page (useSeo). */
import type { Lang } from "../data/types";
import { HEAT_BAN, INDUSTRIES, industryOf, type Industry } from "../domain/industry";
import { FAQ, MODULES, faqFor } from "../pages/public/homeContent";
import { countryOfRoute, inCountry, stripCountry } from "../pages/public/mktCountry";
import { inName, industryIn, isSaudi, permitsShort } from "../pages/public/mktLocal";
import { COUNTRIES, type CountryInfo } from "../domain/country";
import { ANNUAL_DISCOUNT, DEFAULT_PRICES, PLANS, PLAN_INFO, TRIAL_DAYS } from "../pages/public/components/pricingData";
import { OG_SIZE, ogCard, ogImagePath } from "./og";
import { seoPath, seoRoutes } from "./paths";

/** Public origin of the site (canonical URLs, sitemap). Override at build time with VITE_SITE_URL. */
export const SITE_URL: string = ((import.meta as { env?: Record<string, string | undefined> }).env?.VITE_SITE_URL || "https://starthr.gulfunionozone.com").replace(/\/+$/, "");
export const LANGS: Lang[] = ["en", "ar"];
const BRAND: [string, string] = ["StartHR", "ستارت HR"];

export interface PageMeta {
  route: string;
  lang: Lang;
  title: string;
  description: string;
  /** Absolute canonical URL of this language version. */
  url: string;
  /** Absolute URL per language (hreflang). */
  alternates: Record<Lang, string>;
  /** Social preview card (1200×630) and its alt text. */
  image: string;
  imageAlt: string;
  jsonLd: Record<string, unknown>[];
}

export const absUrl = (route: string, lang: Lang) => SITE_URL + seoPath(route, lang);
const pick = (lang: Lang, en: string, ar: string) => (lang === "ar" ? ar : en);

/** Trim to at most `max` characters on a word boundary (meta descriptions show ~155–160 characters). */
export function clip(s: string, max = 160): string {
  const t = s.replace(/\s+/g, " ").trim();
  if (t.length <= max) return t;
  const cut = t.slice(0, max - 1);
  return cut.slice(0, Math.max(cut.lastIndexOf(" "), max - 30)).replace(/[\s,;:·–-]+$/, "") + "…";
}

function organization(lang: Lang) {
  return {
    "@type": "Organization", "@id": SITE_URL + "/#organization", name: BRAND[0], alternateName: BRAND[1], url: SITE_URL + "/",
    logo: { "@type": "ImageObject", url: SITE_URL + "/icon-512.png", width: 512, height: 512 },
    description: pick(lang, "HR and payroll software for Saudi businesses.", "برنامج الموارد البشرية والرواتب للمنشآت السعودية."),
    areaServed: { "@type": "Country", name: "Saudi Arabia" },
  };
}

function software(lang: Lang, extra: Record<string, unknown> = {}) {
  const prices = PLANS.map((p) => DEFAULT_PRICES[p]);
  return {
    "@type": "SoftwareApplication", "@id": SITE_URL + "/#software", name: BRAND[0], applicationCategory: "BusinessApplication",
    applicationSubCategory: "HR and payroll", operatingSystem: "Web, iOS, Android", inLanguage: ["en", "ar"],
    url: SITE_URL + "/", publisher: { "@id": SITE_URL + "/#organization" }, areaServed: { "@type": "Country", name: "Saudi Arabia" },
    offers: {
      "@type": "AggregateOffer", priceCurrency: "SAR", lowPrice: Math.min(...prices), highPrice: Math.max(...prices), offerCount: PLANS.length,
      description: pick(lang, `Per employee per month, excluding 15% VAT. ${TRIAL_DAYS}-day free trial.`, `لكل موظف شهريًا دون ضريبة القيمة المضافة 15%. تجربة مجانية ${TRIAL_DAYS} يومًا.`),
    },
    featureList: MODULES.map((m) => pick(lang, m[1][0], m[1][1])),
    ...extra,
  };
}

function videoObject(l: Lang) {
  return {
    "@type": "VideoObject", name: pick(l, "StartHR in 90 seconds", "ستارت HR في 90 ثانية"),
    description: pick(l, "A short explainer of StartHR: payroll, compliance, attendance and AI for Saudi businesses.", "شرح قصير لستارت HR: الرواتب والامتثال والحضور والذكاء الاصطناعي للمنشآت السعودية."),
    thumbnailUrl: SITE_URL + `/media/starthr-explainer-${l}.jpg`, contentUrl: SITE_URL + `/media/starthr-explainer-${l}.mp4`,
    embedUrl: absUrl("/video", l), uploadDate: "2026-10-01", duration: l === "ar" ? "PT1M43S" : "PT1M35S", inLanguage: l,
  };
}

function breadcrumbs(lang: Lang, items: [string, string][]) {
  return {
    "@type": "BreadcrumbList",
    itemListElement: items.map(([name, route], k) => ({ "@type": "ListItem", position: k + 1, name, item: absUrl(route, lang) })),
  };
}

function industryTitle(ind: Industry, lang: Lang) {
  return pick(lang, `HR & payroll software for ${ind.name[0]} in Saudi Arabia · StartHR`, `برنامج الموارد البشرية والرواتب لقطاع ${ind.name[1]} في السعودية · ستارت HR`);
}

/** Metadata of another GCC country's home, industry index or industry page. */
function countryMeta(route: string, lang: Lang, c: CountryInfo, base: Omit<PageMeta, "title" | "description" | "jsonLd">): PageMeta {
  const L = (en: string, ar: string) => pick(lang, en, ar);
  const the = inName(c);
  const page = stripCountry(route);
  const home = inCountry("/home", c), index = inCountry("/industries", c);
  const homeName = L(`Home · ${c.short[0]}`, `الرئيسية · ${c.short[1]}`);
  const indName = L("Industries", "القطاعات");
  const area = { "@type": "Country", name: c.name[0] };
  if (page === "/home") {
    return {
      ...base,
      title: L(`StartHR · Payroll, ${c.si.short[0]} and WPS for businesses in ${the[0]}`, `ستارت HR · الرواتب و${c.si.short[1]} وحماية الأجور للمنشآت في ${the[1]}`),
      description: clip(L(
        `Bilingual HR and payroll software for companies in ${the[0]}: ${c.si.short[0]}, the ${c.wps.file[0]}, ${c.localisation.short[0]}, end-of-service, biometric attendance and AI data entry. 30 days free.`,
        `برنامج موارد بشرية ورواتب ثنائي اللغة للمنشآت في ${the[1]}: ${c.si.short[1]} و${c.wps.file[1]} و${c.localisation.short[1]} ونهاية الخدمة والحضور بالبصمة والإدخال بالذكاء الاصطناعي. 30 يومًا مجانًا.`), 200),
      jsonLd: [
        organization(lang), software(lang, { "@id": base.url + "#software", areaServed: area }),
        { "@type": "FAQPage", mainEntity: faqFor(c).map(([q, a]) => ({ "@type": "Question", name: L(...q), acceptedAnswer: { "@type": "Answer", text: L(...a) } })) },
      ],
    };
  }
  if (page === "/industries") {
    return {
      ...base,
      title: L(`Industry packs · HR & payroll for every sector in ${the[0]} · StartHR`, `الحزم القطاعية · موارد بشرية ورواتب لكل قطاع في ${the[1]} · ستارت HR`),
      description: clip(L(
        `${INDUSTRIES.length} industry packs for businesses in ${the[0]}: certificates, shifts, site costing and pay elements, with ${c.si.short[0]} and the WPS file built in — for construction, healthcare, retail and more.`,
        `${INDUSTRIES.length} حزمة قطاعية للمنشآت في ${the[1]}: الشهادات والورديات وتكلفة المواقع وعناصر الأجر مع ${c.si.short[1]} وملف حماية الأجور — للمقاولات والرعاية الصحية والتجزئة وغيرها.`), 200),
      jsonLd: [
        organization(lang),
        breadcrumbs(lang, [[homeName, home], [indName, index]]),
        {
          "@type": "ItemList", name: L(`StartHR industry packs in ${the[0]}`, `حزم ستارت HR القطاعية في ${the[1]}`),
          itemListElement: INDUSTRIES.map((x, k) => ({ "@type": "ListItem", position: k + 1, name: L(...x.name), url: absUrl(inCountry("/industries/" + x.key, c), lang) })),
        },
      ],
    };
  }
  const ind = industryIn(industryOf(page.replace("/industries/", ""))!, c);
  const name = L(...ind.name);
  return {
    ...base,
    title: L(`HR & payroll software for ${ind.name[0]} in ${the[0]} · StartHR`, `برنامج الموارد البشرية والرواتب لقطاع ${ind.name[1]} في ${the[1]} · ستارت HR`),
    description: clip(L(`${ind.tagline[0]} For ${the[0]}: ${c.si.short[0]}, WPS and ${c.localisation.short[0]} built in. ${ind.summary[0]}`, `${ind.tagline[1]} في ${the[1]}: ${c.si.short[1]} وحماية الأجور و${c.localisation.short[1]}. ${ind.summary[1]}`)),
    jsonLd: [
      organization(lang),
      breadcrumbs(lang, [[homeName, home], [indName, index], [name, route]]),
      software(lang, {
        "@id": base.url + "#software", name: L(`StartHR for ${ind.name[0]} in ${the[0]}`, `ستارت HR لقطاع ${ind.name[1]} في ${the[1]}`),
        description: L(...ind.summary), audience: { "@type": "BusinessAudience", name }, areaServed: area,
        featureList: ind.features.map((f) => L(...f.title) + (f.status === "live" ? "" : L(" (roadmap)", " (ضمن خطة التطوير)"))),
      }),
    ],
  };
}

/** Metadata for one marketing route in one language; null for routes without a crawlable page. */
export function pageMeta(route: string, lang: Lang): PageMeta | null {
  if (!seoRoutes().includes(route)) return null;
  const L = (en: string, ar: string) => pick(lang, en, ar);
  const homeName = L("Home", "الرئيسية");
  const indName = L("Industries", "القطاعات");
  const base = {
    route, lang, url: absUrl(route, lang),
    alternates: { en: absUrl(route, "en"), ar: absUrl(route, "ar") },
    image: SITE_URL + ogImagePath(route, lang),
    imageAlt: ((c) => `${BRAND[lang === "ar" ? 1 : 0]}: ${c.title}`)(ogCard(route, lang)),
  };
  const country = countryOfRoute(route);
  if (!isSaudi(country)) return countryMeta(route, lang, country, base);
  const website = { "@type": "WebSite", "@id": SITE_URL + "/#website", name: BRAND[0], url: SITE_URL + "/", inLanguage: ["en", "ar"], publisher: { "@id": SITE_URL + "/#organization" } };

  if (route === "/home") {
    return {
      ...base,
      title: L("StartHR · Payroll, GOSI and Iqamas for Saudi businesses", "ستارت HR · الرواتب والتأمينات والإقامات للمنشآت السعودية"),
      description: L(
        "Bilingual HR and payroll software for Saudi companies: GOSI, WPS and Mudad, Qiwa and Nitaqat, Muqeem iqama renewals, biometric attendance, self-service and AI data entry. 30 days free.",
        "برنامج موارد بشرية ورواتب ثنائي اللغة للمنشآت السعودية: التأمينات وحماية الأجور ومدد وقوى ونطاقات وتجديد الإقامات في مقيم والحضور بالبصمة والخدمة الذاتية والإدخال بالذكاء الاصطناعي. 30 يومًا مجانًا."),
      jsonLd: [
        organization(lang), website, software(lang),
        videoObject(lang),
        { "@type": "FAQPage", mainEntity: FAQ.map(([q, a]) => ({ "@type": "Question", name: L(...q), acceptedAnswer: { "@type": "Answer", text: L(...a) } })) },
      ],
    };
  }

  if (route === "/industries") {
    return {
      ...base,
      title: L("Industry packs · HR & payroll for every Saudi sector · StartHR", "الحزم القطاعية · موارد بشرية ورواتب لكل قطاع سعودي · ستارت HR"),
      description: L(
        `${INDUSTRIES.length} industry packs for Saudi businesses: certificates, Saudization quotas, shifts, site costing and pay elements for construction, healthcare, restaurants, retail and more.`,
        `${INDUSTRIES.length} حزمة قطاعية للمنشآت السعودية: الشهادات ونسب التوطين والورديات وتكلفة المواقع وعناصر الأجر للمقاولات والرعاية الصحية والمطاعم والتجزئة وغيرها.`),
      jsonLd: [
        organization(lang),
        breadcrumbs(lang, [[homeName, "/home"], [indName, "/industries"]]),
        {
          "@type": "ItemList", name: L("StartHR industry packs", "حزم ستارت HR القطاعية"),
          itemListElement: INDUSTRIES.map((c, k) => ({ "@type": "ListItem", position: k + 1, name: L(...c.name), url: absUrl("/industries/" + c.key, lang) })),
        },
      ],
    };
  }

  if (route === "/pricing") {
    const plans = PLANS.map((p) => `${p} SAR ${DEFAULT_PRICES[p]}`).join(", ");
    return {
      ...base,
      title: L("Pricing · per-employee HR & payroll plans · StartHR", "الأسعار · باقات الموارد البشرية والرواتب لكل موظف · ستارت HR"),
      description: L(
        `Simple per-employee pricing: ${plans} a month. ${ANNUAL_DISCOUNT * 100}% off yearly, ${TRIAL_DAYS}-day free trial, bank transfer with a ZATCA tax invoice.`,
        `أسعار بسيطة لكل موظف شهريًا: ${PLANS.map((p) => `${p} ${DEFAULT_PRICES[p]} ريال`).join("، ")}. خصم ${ANNUAL_DISCOUNT * 100}% للدفع السنوي وتجربة مجانية ${TRIAL_DAYS} يومًا وفاتورة ضريبية.`),
      jsonLd: [
        organization(lang),
        breadcrumbs(lang, [[homeName, "/home"], [L("Pricing", "الأسعار"), "/pricing"]]),
        {
          "@type": "Product", name: BRAND[0], brand: { "@id": SITE_URL + "/#organization" },
          description: L("HR and payroll software for Saudi businesses, priced per employee per month.", "برنامج الموارد البشرية والرواتب للمنشآت السعودية بسعر لكل موظف شهريًا."),
          offers: PLANS.map((p) => ({
            "@type": "Offer", name: p, price: DEFAULT_PRICES[p], priceCurrency: "SAR", url: base.url,
            description: L(...PLAN_INFO[p].limit) + " · " + L("per employee per month, excluding VAT", "لكل موظف شهريًا دون الضريبة"),
          })),
        },
      ],
    };
  }



  if (route === "/video") {
    return {
      ...base,
      title: L("StartHR in 90 seconds · Video tour of Saudi HR & payroll software", "ستارت HR في 90 ثانية · جولة مصورة في برنامج الموارد البشرية والرواتب"),
      description: L(
        "Watch how StartHR takes a Saudi business from piles of paper to payroll in one click: GOSI, WPS, Iqamas, biometric attendance and AI, in Arabic and English.",
        "شاهد كيف ينقل ستارت HR المنشأة السعودية من أكوام الأوراق إلى الرواتب بضغطة واحدة: التأمينات وحماية الأجور والإقامات والحضور بالبصمة والذكاء الاصطناعي بالعربية والإنجليزية."),
      jsonLd: [organization(lang), breadcrumbs(lang, [[homeName, "/home"], [L("Video", "الفيديو"), "/video"]]), videoObject(lang)],
    };
  }

  if (route === "/login") {
    return {
      ...base,
      title: L("Sign in · StartHR HR & payroll", "تسجيل الدخول · ستارت HR للموارد البشرية والرواتب"),
      description: L(
        "Sign in to StartHR, the bilingual HR and payroll system for Saudi businesses, or open the live demo as an owner, HR manager, accountant or employee.",
        "سجّل الدخول إلى ستارت HR، نظام الموارد البشرية والرواتب ثنائي اللغة للمنشآت السعودية، أو افتح النسخة التجريبية كمالك أو مدير موارد بشرية أو محاسب أو موظف."),
      jsonLd: [organization(lang), breadcrumbs(lang, [[homeName, "/home"], [L("Sign in", "تسجيل الدخول"), "/login"]])],
    };
  }

  if (route === "/setup") {
    return {
      ...base,
      title: L(`Start your ${TRIAL_DAYS}-day free trial · StartHR sign-up`, `ابدأ تجربتك المجانية ${TRIAL_DAYS} يومًا · التسجيل في ستارت HR`),
      description: L(
        `Set up your company on StartHR in minutes: company details, branches, payroll settings and employees. ${TRIAL_DAYS} days free with every feature, no card needed.`,
        `أنشئ منشأتك على ستارت HR خلال دقائق: بيانات المنشأة والفروع وإعدادات الرواتب والموظفين. ${TRIAL_DAYS} يومًا مجانًا بكل المزايا دون بطاقة.`),
      jsonLd: [organization(lang), breadcrumbs(lang, [[homeName, "/home"], [L("Sign up", "التسجيل"), "/setup"]]), software(lang)],
    };
  }

  const ind = industryOf(route.replace("/industries/", ""))!;
  const name = L(...ind.name);
  return {
    ...base,
    title: industryTitle(ind, lang),
    description: clip(L(...ind.tagline) + " " + L(...ind.summary)),
    jsonLd: [
      organization(lang),
      breadcrumbs(lang, [[homeName, "/home"], [indName, "/industries"], [name, route]]),
      software(lang, {
        "@id": base.url + "#software", name: L(`StartHR for ${ind.name[0]}`, `ستارت HR لقطاع ${ind.name[1]}`),
        description: L(...ind.summary), audience: { "@type": "BusinessAudience", name },
        featureList: ind.features.map((f) => L(...f.title) + (f.status === "live" ? "" : L(" (roadmap)", " (ضمن خطة التطوير)"))),
      }),
    ],
  };
}

/** Every crawlable page in both languages. */
export const allPages = (): PageMeta[] => seoRoutes().flatMap((r) => LANGS.map((l) => pageMeta(r, l)!));

const esc = (s: string) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

/** <head> tags for a prerendered page (title, description, canonical, hreflang, Open Graph, Twitter, JSON-LD). */
export function headTags(m: PageMeta): string {
  const ld = JSON.stringify({ "@context": "https://schema.org", "@graph": m.jsonLd }).replace(/</g, "\\u003c");
  return [
    `<title>${esc(m.title)}</title>`,
    `<meta name="description" content="${esc(m.description)}">`,
    `<meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1">`,
    `<link rel="canonical" href="${m.url}">`,
    `<link rel="alternate" hreflang="en" href="${m.alternates.en}">`,
    `<link rel="alternate" hreflang="ar" href="${m.alternates.ar}">`,
    `<link rel="alternate" hreflang="x-default" href="${m.alternates.en}">`,
    `<meta property="og:type" content="website">`,
    `<meta property="og:site_name" content="${BRAND[0]}">`,
    `<meta property="og:title" content="${esc(m.title)}">`,
    `<meta property="og:description" content="${esc(m.description)}">`,
    `<meta property="og:url" content="${m.url}">`,
    `<meta property="og:image" content="${m.image}">`,
    `<meta property="og:image:type" content="image/png">`,
    `<meta property="og:image:width" content="${OG_SIZE.width}">`,
    `<meta property="og:image:height" content="${OG_SIZE.height}">`,
    `<meta property="og:image:alt" content="${esc(m.imageAlt)}">`,
    `<meta property="og:locale" content="${m.lang === "ar" ? "ar_SA" : "en_US"}">`,
    `<meta property="og:locale:alternate" content="${m.lang === "ar" ? "en_US" : "ar_SA"}">`,
    `<meta name="twitter:card" content="summary_large_image">`,
    `<meta name="twitter:title" content="${esc(m.title)}">`,
    `<meta name="twitter:description" content="${esc(m.description)}">`,
    `<meta name="twitter:image" content="${m.image}">`,
    `<meta name="twitter:image:alt" content="${esc(m.imageAlt)}">`,
    `<link rel="alternate" type="text/plain" title="LLM summary" href="${SITE_URL}/llms.txt">`,
    `<script type="application/ld+json">${ld}</script>`,
  ].join("\n");
}

/** sitemap.xml with hreflang alternates for every language version. */
export function sitemapXml(lastmod: string): string {
  const urls = allPages().map((m) => [
    "  <url>",
    `    <loc>${m.url}</loc>`,
    `    <lastmod>${lastmod}</lastmod>`,
    `    <changefreq>${stripCountry(m.route) === "/home" ? "weekly" : "monthly"}</changefreq>`,
    `    <priority>${m.route === "/home" ? "1.0" : stripCountry(m.route) === "/home" || m.route === "/industries" || m.route === "/pricing" ? "0.9" : "0.8"}</priority>`,
    `    <xhtml:link rel="alternate" hreflang="en" href="${m.alternates.en}"/>`,
    `    <xhtml:link rel="alternate" hreflang="ar" href="${m.alternates.ar}"/>`,
    `    <xhtml:link rel="alternate" hreflang="x-default" href="${m.alternates.en}"/>`,
    "  </url>",
  ].join("\n"));
  return `<?xml version="1.0" encoding="UTF-8"?>\n<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9" xmlns:xhtml="http://www.w3.org/1999/xhtml">\n${urls.join("\n")}\n</urlset>\n`;
}

/** AI crawlers named explicitly so their operators see they are welcome on the marketing pages. */
export const AI_BOTS = ["GPTBot", "OAI-SearchBot", "ChatGPT-User", "ClaudeBot", "Claude-SearchBot", "Claude-User", "anthropic-ai", "PerplexityBot", "Perplexity-User", "Google-Extended", "Applebot-Extended", "Bingbot", "CCBot", "Meta-ExternalAgent", "Amazonbot", "DuckAssistBot", "cohere-ai", "MistralAI-User"];

export function robotsTxt(): string {
  const rules = ["Allow: /", "Disallow: /api/", "Disallow: /iclock/", "Disallow: /downloads/"];
  return [
    "# StartHR — public marketing pages are open to search engines and AI assistants.",
    "# A plain-text summary for language models: " + SITE_URL + "/llms.txt",
    "",
    "User-agent: *", ...rules, "",
    ...AI_BOTS.flatMap((b) => [`User-agent: ${b}`, ...rules, ""]),
    `Sitemap: ${SITE_URL}/sitemap.xml`,
    "",
  ].join("\n");
}

const live = (c: Industry) => c.features.filter((f) => f.status === "live");

/** llms.txt (https://llmstxt.org): what StartHR is, with links to every page and the full-text companion. */
export function llmsTxt(): string {
  const out = [
    "# StartHR",
    "",
    "> StartHR is a bilingual (English/Arabic) HR and payroll SaaS for companies in Saudi Arabia. It runs payroll with GOSI, WPS/Mudad files, Qiwa contract and Nitaqat (Saudization) tracking, Muqeem iqama renewals, biometric attendance, employee self-service and AI-assisted data entry, with data hosted in Saudi Arabia (PDPL).",
    "",
    `Pricing is per employee per month (excluding 15% VAT): ${PLANS.map((p) => `${p} SAR ${DEFAULT_PRICES[p]} (${PLAN_INFO[p].limit[0].toLowerCase()})`).join(", ")}. Yearly billing is ${ANNUAL_DISCOUNT * 100}% cheaper. Every plan starts with a ${TRIAL_DAYS}-day free trial with no card; payment is by Saudi bank transfer with a ZATCA tax invoice.`,
    "",
    `Industry packs adapt StartHR to ${INDUSTRIES.length} sectors (certificates and licences, Saudization quotas, recommended shifts, the summer midday outdoor-work ban, site or branch costing and pay elements). Each pack lists its features honestly as live or on the roadmap.`,
    "",
    "## Main pages",
    "",
    `- [Home](${absUrl("/home", "en")}): product overview, modules, compliance, biometric devices, calculators, plans and FAQ`,
    `- [Industry packs](${absUrl("/industries", "en")}): all ${INDUSTRIES.length} industries compared side by side`,
    `- [Pricing](${absUrl("/pricing", "en")}): plans and a subscription calculator`,
    `- [Video](${absUrl("/video", "en")}): StartHR in 90 seconds`,
    `- [Start a free trial](${absUrl("/setup", "en")}): ${TRIAL_DAYS} days free, company set up in minutes`,
    `- [Sign in / live demo](${absUrl("/login", "en")}): sign in, or open the demo company by role`,
    `- [Full text for language models](${SITE_URL}/llms-full.txt): every industry pack in detail, modules and FAQ`,
    "",
    "## Industry packs",
    "",
    ...INDUSTRIES.map((c) => `- [${c.name[0]}](${absUrl("/industries/" + c.key, "en")}): ${c.tagline[0]} (${live(c).length} live features, ${c.features.length - live(c).length} on the roadmap)`),
    "",
    "## GCC countries",
    "",
    "StartHR also serves companies in the other GCC countries, with each country's social insurance, wage protection, end-of-service, leave and localisation rules. Each country has its own home page, industry index and industry pages (English and Arabic):",
    "",
    ...COUNTRIES.filter((c) => c.slug).flatMap((c) => [
      `- [${c.name[0]}](${absUrl(inCountry("/home", c), "en")}): ${c.si.short[0]}, ${c.wps.name[0]}, ${c.localisation.name[0]}, ${c.currency.code} · [industry packs](${absUrl(inCountry("/industries", c), "en")}) · [Arabic](${absUrl(inCountry("/home", c), "ar")})`,
    ]),
    "",
    "## Arabic",
    "",
    `- [الصفحة الرئيسية](${absUrl("/home", "ar")})`,
    `- [الحزم القطاعية](${absUrl("/industries", "ar")})`,
    `- [الأسعار](${absUrl("/pricing", "ar")})`,
    `- [الفيديو](${absUrl("/video", "ar")})`,
    `- [ابدأ مجانًا](${absUrl("/setup", "ar")})`,
    `- [تسجيل الدخول](${absUrl("/login", "ar")})`,
    ...INDUSTRIES.map((c) => `- [${c.name[1]}](${absUrl("/industries/" + c.key, "ar")}): ${c.tagline[1]}`),
    "",
    "## Optional",
    "",
    `- [Sitemap](${SITE_URL}/sitemap.xml)`,
    "",
  ];
  return out.join("\n");
}

const DAY_EN = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

/** llms-full.txt: the marketing content as Markdown, so an assistant can answer detailed questions in one fetch. */
export function llmsFullTxt(): string {
  const out = [llmsTxt().split("## Main pages")[0].trimEnd(), "", "## Core modules (every plan and industry)", ""];
  for (const m of MODULES) out.push(`- **${m[1][0]}**: ${m[2][0]}`);
  out.push("", "## Plans", "");
  for (const p of PLANS) out.push(`- **${p}** — SAR ${DEFAULT_PRICES[p]} per employee per month; ${PLAN_INFO[p].limit[0]}. ${PLAN_INFO[p].features.map((f) => f[0]).join("; ")}.`);
  out.push("", "## Frequently asked questions", "");
  for (const [q, a] of FAQ) out.push(`### ${q[0]}`, "", a[0], "");
  out.push("## GCC countries", "");
  for (const c of COUNTRIES) {
    out.push(`### ${c.name[0]} (${c.name[1]})`, "", `Home: ${absUrl(inCountry("/home", c), "en")} · Industry packs: ${absUrl(inCountry("/industries", c), "en")}`, "");
    out.push(`- Labour law: ${c.laborLaw[0]}`, `- Social insurance (${c.si.short[0]}): ${c.si.summary[0]}`, `- End of service: ${c.eosb.summary[0]}`,
      `- Annual leave: ${c.leave.annual[0]} days${c.leave.annual[1] !== c.leave.annual[0] ? `, rising to ${c.leave.annual[1]}` : ""}`, `- Wage protection: ${c.wps.name[0]} (${c.wps.file[0]})`,
      `- Labour ministry: ${c.ministry.name[0]}; residence and permits: ${permitsShort(c)[0]}`, `- Localisation: ${c.localisation.name[0]} — ${c.localisation.summary[0]}`,
      `- Summer midday outdoor-work ban: ${c.heatBan.start}–${c.heatBan.end}, ${c.heatBan.from} to ${c.heatBan.to} (MM-DD)`, `- Currency: ${c.currency.code}; ${c.hosting[0]}`, "");
  }
  out.push("## Industry packs in detail", "");
  for (const c of INDUSTRIES) {
    out.push(`### ${c.name[0]} (${c.name[1]})`, "", `URL: ${absUrl("/industries/" + c.key, "en")} · Arabic: ${absUrl("/industries/" + c.key, "ar")}`, "", c.tagline[0], "", c.summary[0], "");
    out.push("Key facts: " + c.stats.map(([v, l]) => `${v} ${l[0]}`).join("; ") + ".", "");
    out.push("Problems it solves:", ...c.pains.map((p) => `- ${p[0]}`), "");
    out.push("Features:", ...c.features.map((f) => `- ${f.title[0]} (${f.status === "live" ? "live" : "roadmap"}): ${f.text[0]}`), "");
    out.push("Certificates and licences tracked:", ...c.certs.map((x) => `- ${x.kind}${x.note ? ": " + x.note[0] : ""}`), "");
    if (c.quotas.length) out.push("Saudization targets:", ...c.quotas.map((q) => `- ${q.group[0]}: ${q.pct}% from ${q.minStaff} staff in the group; Saudis count from SAR ${q.minWage.toLocaleString("en")} a month${q.note ? ". " + q.note[0] : ""}`), "");
    out.push(`Recommended working time: ${c.shift.start}–${c.shift.end}, weekend ${c.shift.weekend.map((d) => DAY_EN[d]).join(" & ")}. ${c.shift.note[0]}`);
    if (c.heat) out.push(`Summer midday ban: no outdoor work ${HEAT_BAN.start}–${HEAT_BAN.end} between ${HEAT_BAN.from} and ${HEAT_BAN.to} (MM-DD); StartHR checks outdoor sites' shifts against it.`);
    out.push(`Sites module: ${c.sites.label[0]}${c.sites.contract ? " with contract value and margin" : ""}.`, `Pay elements: ${c.pay.map((p) => p[0]).join(", ")}.`, "");
  }
  return out.join("\n");
}
